import { Link } from '@tanstack/react-router';
import { Fragment, type ReactNode } from 'react';
import { when } from '../lib/datetime';
import {
  formatBytes,
  reportSummary,
  type DevSaveLoadReport,
  type DevSaveRow,
} from '../lib/devSaves';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';

export const saveBtn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/40 disabled:opacity-40';

/** Kind plates: yellow = manual, cyan = import, plain = automatic (before a load/reset). */
const kindCls: Record<string, string> = {
  manual: 'bg-caption',
  import: 'bg-benday',
};

/**
 * Save points as a table (name, description, source player, kind, created, rows, size, schema), with per-row
 * actions and an optional expanded row (the "Load onto…" picker). Used by the Save points page and the player detail.
 */
export function SaveTable({
  rows,
  actions,
  expanded,
}: {
  rows: DevSaveRow[];
  actions: (row: DevSaveRow) => ReactNode;
  expanded?: (row: DevSaveRow) => ReactNode;
}) {
  if (rows.length === 0) return <p className="mt-2 text-sm">No save points.</p>;
  return (
    <table className="mt-2 w-full text-xs">
      <thead className="text-bruise">
        <tr>
          <th className={th}>Name</th>
          <th className={th}>Source player</th>
          <th className={th}>Kind</th>
          <th className={th}>Created</th>
          <th className={th}>Rows</th>
          <th className={th}>Size</th>
          <th className={th}>Schema</th>
          <th className={th} />
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => {
          const more = expanded?.(s);
          return (
            <Fragment key={s.id}>
              <tr className="border-t border-newsprint align-top">
                <td className={td}>
                  <div className="font-semibold">{s.name}</div>
                  {s.description && <div className="text-bruise">{s.description}</div>}
                </td>
                <td className={td}>
                  <Link
                    to="/players/$playerId"
                    params={{ playerId: s.sourcePlayerId }}
                    className="underline"
                  >
                    {s.sourceLabel || s.sourcePlayerId}
                  </Link>
                  {s.forPlayerId && s.forPlayerId !== s.sourcePlayerId && (
                    <div className="text-bruise">
                      for{' '}
                      <Link
                        to="/players/$playerId"
                        params={{ playerId: s.forPlayerId }}
                        className="underline"
                      >
                        {s.forPlayerId}
                      </Link>
                    </div>
                  )}
                </td>
                <td className={td}>
                  <span
                    className={`rounded px-1.5 py-0.5 font-semibold ${kindCls[s.kind] ?? 'bg-newsprint'}`}
                  >
                    {s.kind}
                  </span>
                </td>
                <td className={`${td} font-mono`}>
                  <div>{when(s.createdAt)}</div>
                  <div className="text-bruise">{s.createdBy}</div>
                </td>
                <td className={`${td} font-mono`}>
                  {s.rowCount} in {s.tableCount} tables
                </td>
                <td className={`${td} font-mono`}>{formatBytes(s.sizeBytes)}</td>
                <td className={`${td} font-mono`} title={`content v${s.contentVersion}`}>
                  {s.schemaMigration}
                  <div className="text-bruise">content v{s.contentVersion}</div>
                </td>
                <td className={td}>
                  <div className="flex flex-wrap gap-1">{actions(s)}</div>
                </td>
              </tr>
              {more && (
                <tr className="bg-newsprint/40">
                  <td colSpan={8} className="px-2 py-2">
                    {more}
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

/** What a load, reset or undo did: totals, remap, warnings, and the per-table counts. */
export function LoadReportView({
  title,
  report,
  onClose,
}: {
  title: string;
  report: DevSaveLoadReport;
  onClose: () => void;
}) {
  return (
    <div className="mt-3 rounded border-2 border-ink bg-benday/20 p-3 text-sm" role="status">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold">{title}</div>
          <div>{reportSummary(report)}</div>
          <div className="text-xs text-bruise">
            Undo save <span className="font-mono">{report.undoSaveId}</span> was taken first.
          </div>
        </div>
        <button type="button" className={saveBtn} onClick={onClose}>
          Close
        </button>
      </div>
      {report.warnings.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-blam">
          {report.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
      {report.tables.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs font-semibold">Per table</summary>
          <table className="mt-1 text-xs">
            <thead className="text-bruise">
              <tr>
                <th className={th}>Table</th>
                <th className={th}>Removed</th>
                <th className={th}>In save</th>
                <th className={th}>Inserted</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {report.tables.map((t) => (
                <tr key={t.table} className="border-t border-newsprint">
                  <td className={td}>{t.table}</td>
                  <td className={td}>{t.removed}</td>
                  <td className={td}>{t.rows}</td>
                  <td className={td}>{t.inserted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
